import React, { useEffect, useMemo, useState } from 'react';
import {
  Typography, Table, TableHead, TableRow, TableCell, TableBody, TextField,
} from '@mui/material';

import SectionCard from './SectionCard';
import { TableCard, tableHeadCellSx } from '../tabLayout';
import { CopyToAllButton, QuietNote, SchoolClass, copyToAll, numSx, useClassNames } from './sectionParts';
import { AdmissionCycle, AssessmentPathway, TestSitting } from '../../../types/admissions';
import dateTimeUtils from '../../../utils/dateTimeUtils';
import { assessmentNoun, assessmentPlan, pathwayFor } from '../../../utils/admissionAssessment';

interface Props {
  cycle: AdmissionCycle;
  /** Only this pathway's classes are listed and saved. */
  pathway: AssessmentPathway;
  classes: SchoolClass[];
  canEdit: boolean;
  onSave: (patch: Partial<AdmissionCycle>) => Promise<void>;
}

/**
 * When and where each of the pathway's classes is assessed — one row per
 * class in the seat matrix. Admit cards and the parents' status page read it
 * live, so it stays editable after the cycle opens: a reschedule here
 * reaches every card without re-issuing.
 */

/** One sitting per class on the pathway, when the pathway assesses at all —
 *  by test, interview or observation. */
const sittingsFor = (cycle: AdmissionCycle, pathway: AssessmentPathway): TestSitting[] => {
  if (!assessmentPlan(pathway).hasAssessment) return [];
  const byClass = new Map((cycle.testSchedule ?? []).map(s => [s.classId, s]));
  return (cycle.seatMatrix ?? []).filter(e => pathwayFor(cycle, e.classId).key === pathway.key).map(e => ({
    classId: e.classId,
    date: byClass.get(e.classId)?.date ? String(byClass.get(e.classId)!.date).slice(0, 10) : '',
    reportingTime: byClass.get(e.classId)?.reportingTime ?? '',
    venue: byClass.get(e.classId)?.venue ?? '',
  }));
};

const NotSet: React.FC = () => <Typography component="span" variant="body2" color="text.disabled">Not set</Typography>;

const TestScheduleSection: React.FC<Props> = ({ cycle, pathway, classes, canEdit, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState<TestSitting[]>([]);
  const { nameOf } = useClassNames(classes);
  const saved = useMemo(() => sittingsFor(cycle, pathway), [cycle, pathway]);
  const multiple = (cycle.pathways || []).length > 1;

  useEffect(() => {
    if (editing) setDraft(saved);
  }, [editing, saved]);

  const rows = editing ? draft : saved;

  const update = (idx: number, patch: Partial<TestSitting>) =>
    setDraft(d => d.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const copyRow = (idx: number) =>
    setDraft(d => copyToAll(d, idx, r => ({ date: r.date, reportingTime: r.reportingTime, venue: r.venue })));

  const handleSave = async () => {
    setSaving(true);
    try {
      // Other pathways' classes keep their sittings untouched.
      const here = new Set(saved.map(r => r.classId));
      await onSave({
        testSchedule: [
          ...(cycle.testSchedule ?? []).filter(s => !here.has(s.classId)),
          ...draft.map(r => ({ classId: r.classId, date: r.date || null, reportingTime: r.reportingTime || undefined, venue: r.venue?.trim() || undefined }))
            .filter(r => r.date || r.reportingTime || r.venue),
        ],
      });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionCard
      title="Assessment schedule"
      description={`When and where each class${multiple ? ' on this pathway' : ''} is assessed. Admit cards always show what's set here, so a change reaches every card.`}
      canEdit={canEdit && rows.length > 0}
      editing={editing}
      onEditToggle={setEditing}
      onSave={handleSave}
      onCancel={() => setDraft(saved)}
      saving={saving}
    >
      {!assessmentPlan(pathway).hasAssessment && !editing ? (
        <QuietNote>{multiple ? "This pathway isn't assessed" : 'No class is assessed'}. Turn on a test, interview or observation under Stages.</QuietNote>
      ) : rows.length === 0 ? (
        <QuietNote>{multiple ? 'No class on this pathway has seats yet' : 'Add classes under Seats first'} — each assessed class gets its own date, time and venue.</QuietNote>
      ) : (
        <TableCard>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={tableHeadCellSx}>Class</TableCell>
                <TableCell sx={tableHeadCellSx}>Date</TableCell>
                <TableCell sx={tableHeadCellSx}>Reporting time</TableCell>
                <TableCell sx={tableHeadCellSx}>Venue</TableCell>
                {editing && rows.length > 1 && <TableCell sx={tableHeadCellSx} />}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, idx) => (
                <TableRow key={row.classId} sx={{ '&:last-of-type td': { borderBottom: 0 } }}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Typography variant="body2" fontWeight={500}>{nameOf(row.classId)}</Typography>
                    <Typography variant="caption" color="text.secondary">{assessmentNoun(pathwayFor(cycle, row.classId))}</Typography>
                  </TableCell>
                  {editing ? (
                    <>
                      <TableCell>
                        <TextField
                          type="date" size="small" fullWidth sx={{ minWidth: 150 }}
                          value={row.date || ''}
                          onChange={(e) => update(idx, { date: e.target.value })}
                          inputProps={{ 'aria-label': `${nameOf(row.classId)} assessment date` }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="time" size="small" fullWidth sx={{ minWidth: 120 }}
                          value={row.reportingTime || ''}
                          onChange={(e) => update(idx, { reportingTime: e.target.value })}
                          inputProps={{ 'aria-label': `${nameOf(row.classId)} reporting time` }}
                        />
                      </TableCell>
                      <TableCell sx={{ width: '40%' }}>
                        <TextField
                          size="small" fullWidth sx={{ minWidth: 180 }}
                          placeholder="e.g. Main campus, Hall A"
                          value={row.venue || ''}
                          onChange={(e) => update(idx, { venue: e.target.value })}
                          inputProps={{ maxLength: 200, 'aria-label': `${nameOf(row.classId)} venue` }}
                        />
                      </TableCell>
                      {rows.length > 1 && (
                        <TableCell align="right" padding="none" sx={{ pr: 1 }}>
                          <CopyToAllButton
                            title={multiple ? 'Use for every class on this pathway' : undefined}
                            onClick={() => copyRow(idx)}
                          />
                        </TableCell>
                      )}
                    </>
                  ) : (
                    <>
                      <TableCell sx={{ ...numSx, whiteSpace: 'nowrap' }}>
                        {row.date ? dateTimeUtils.formatDateForDisplay(row.date, undefined, 'EEE, dd MMM yyyy') : <NotSet />}
                      </TableCell>
                      <TableCell sx={{ ...numSx, whiteSpace: 'nowrap' }}>
                        {row.reportingTime ? dateTimeUtils.formatClockTime(row.reportingTime) : <NotSet />}
                      </TableCell>
                      <TableCell>{row.venue || <NotSet />}</TableCell>
                    </>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableCard>
      )}
    </SectionCard>
  );
};

export default TestScheduleSection;
